// Global application state store using Zustand
// Implements centralized state management as per Studio1 Architecture

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { logger } from "../services/logger";
import { generateUUID } from "../utils/uuid";
import type { User, Team, Project, Workflow } from "../types";
import type { UserOrganisation } from "../types/organisation";
import type { EntitlementResult } from "../types/entitlements";
import { useEntitlementDebugStore } from "./useEntitlementDebugStore";

interface AppState {
  // State
  user: User | null;
  organisation: UserOrganisation | null;
  teams: Team[];
  currentTeam: Team | null;
  projects: Project[];
  currentProject: Project | null;
  workflows: Workflow[];
  entitlements: EntitlementResult | null;
  entitlementOrigin: string | null;
  entitlementUpdatedAt: number | null;
  isLoading: boolean;
  error: string | null;
}

interface AppStore extends AppState {
  // Actions
  setUser: (user: User | null) => void;
  setOrganisation: (org: UserOrganisation | null) => void;
  setTeams: (teams: Team[]) => void;
  setCurrentTeam: (team: Team | null) => void;
  setProjects: (projects: Project[]) => void;
  setCurrentProject: (project: Project | null) => void;
  setWorkflows: (workflows: Workflow[]) => void;
  setEntitlements: (
    entitlements: EntitlementResult | null,
    origin?: string,
  ) => void;
  addWorkflow: (workflow: Workflow) => void;
  updateWorkflow: (id: string, updates: Partial<Workflow>) => void;
  removeWorkflow: (id: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState: AppState = {
  user: null,
  organisation: null,
  teams: [],
  currentTeam: null,
  projects: [],
  currentProject: null,
  workflows: [],
  entitlements: null,
  entitlementOrigin: null,
  entitlementUpdatedAt: null,
  isLoading: false,
  error: null,
};

export const useAppStore = create<AppStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setUser: (user) => {
        logger.info("User state updated", {
          userId: user?.id,
          correlationId: generateUUID(),
        });
        set({ user }, false, "setUser");
      },

      setOrganisation: (org: UserOrganisation | null) => {
        logger.info("Organisation state updated", {
          organisationId: org?.id,
          correlationId: generateUUID(),
        });
        set({ organisation: org }, false, "setOrganisation");
      },

      setTeams: (teams: Team[]) => {
        logger.debug("Teams list updated", {
          count: teams.length,
          correlationId: generateUUID(),
        });
        set({ teams }, false, "setTeams");
      },

      setCurrentTeam: (team: Team | null) => {
        logger.info("Current team updated", {
          teamId: team?.id,
          correlationId: generateUUID(),
        });
        set({ currentTeam: team }, false, "setCurrentTeam");
      },

      setProjects: (projects: Project[]) => {
        logger.debug("Projects list updated", {
          count: projects.length,
          correlationId: generateUUID(),
        });
        set({ projects }, false, "setProjects");
      },

      setCurrentProject: (project: Project | null) => {
        logger.info("Current project updated", {
          projectId: project?.id,
          correlationId: generateUUID(),
        });
        set({ currentProject: project }, false, "setCurrentProject");
      },

      setWorkflows: (workflows: Workflow[]) => {
        logger.debug("Workflows list updated", {
          count: workflows.length,
          correlationId: generateUUID(),
        });
        set({ workflows }, false, "setWorkflows");
      },

      setEntitlements: (
        entitlements: EntitlementResult | null,
        origin?: string,
      ) => {
        const now = Date.now();
        if (import.meta.env.DEV) {
          useEntitlementDebugStore
            .getState()
            .log(
              entitlements ? "set" : "clear",
              origin ?? "appStore.setEntitlements",
              entitlements
                ? `gates:${Object.keys(entitlements.gates).length} caps:${Object.keys(entitlements.caps).length} quotas:${Object.keys(entitlements.quotas).length} budgets:${Object.keys(entitlements.budgets).length}`
                : "cleared",
              entitlements,
            );
        }
        set(
          {
            entitlements,
            entitlementOrigin: origin ?? null,
            entitlementUpdatedAt: now,
          },
          false,
          "setEntitlements",
        );
      },

      addWorkflow: (workflow: Workflow) => {
        const { workflows } = get();
        logger.info("Workflow added", {
          workflowId: workflow.id,
          correlationId: generateUUID(),
        });
        set({ workflows: [...workflows, workflow] }, false, "addWorkflow");
      },

      updateWorkflow: (id: string, updates: Partial<Workflow>) => {
        const { workflows } = get();
        const updatedWorkflows = workflows.map((workflow) =>
          workflow.id === id ? { ...workflow, ...updates } : workflow,
        );
        logger.info("Workflow updated", {
          workflowId: id,
          updates: Object.keys(updates),
          correlationId: generateUUID(),
        });
        set({ workflows: updatedWorkflows }, false, "updateWorkflow");
      },

      removeWorkflow: (id: string) => {
        const { workflows } = get();
        const filteredWorkflows = workflows.filter(
          (workflow) => workflow.id !== id,
        );
        logger.info("Workflow removed", {
          workflowId: id,
          correlationId: generateUUID(),
        });
        set({ workflows: filteredWorkflows }, false, "removeWorkflow");
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading }, false, "setLoading");
      },

      setError: (error: string | null) => {
        if (error) {
          logger.error("Application error set", new Error(error), {
            correlationId: generateUUID(),
          });
        }
        set({ error }, false, "setError");
      },

      clearError: () => {
        set({ error: null }, false, "clearError");
      },

      reset: () => {
        logger.info("Application state reset", {
          correlationId: generateUUID(),
        });
        set(initialState, false, "reset");
      },
    }),
    {
      name: "app-store",
      enabled: import.meta.env.DEV,
    },
  ),
);
